import { useEffect, useState } from "react";
import { IQuestionDto } from "../../../models/dto/trainingApi/IQuestionDto";
import { encryptText } from "../../../utils/encryptionUtils";
import courseService from "../../../services/trainingApi/course.service";
import {
  IAnswerCourseIn,
  IEvaluationCourseIn,
} from "../../../models/input/trainingApi/IEvaluationCourseIn";

export const useEvaluation = () => {
  /* =======================
   STATE – QUESTIONS
======================= */
  const [questions, setQuestions] = useState<IQuestionDto[]>([]);

  /* =======================
   STATE – BUTTONS
======================= */
  const [buttonStartEvaluation, setButtonStartEvaluation] = useState(true);
  const [buttonEvaluate, setButtonEvaluate] = useState(true);

  /* =======================
   STATE – RESULT
======================= */
  const [score, setScore] = useState<number>(0);
  const [certificate, setCertificate] = useState<string>("");

  /* =======================
   STATE – TIMER
======================= */
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const minutes = Math.floor((time % 360000) / 6000);
  const seconds = Math.floor((time % 6000) / 100);

  /* =======================
     EFFECTS
  ======================== */

  // Enable / disable evaluate button
  useEffect(() => {
    if (questions.some((item) => item.answerSelected === "")) {
      setButtonEvaluate(true);
    } else {
      setButtonEvaluate(false);
    }
  }, [questions]);

  // Timer
  useEffect(() => {
    let intervalId: any;

    if (isRunning) {
      intervalId = setInterval(() => {
        setTime((prev) => prev + 100);
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [isRunning]);

  /* =======================
   DATA LOAD / RESET
======================= */

  const loadCertificate = async (courseId: number, languageId: number) => {
    const result = await courseService.getCourseCertificate(
      courseId,
      languageId
    );
    setCertificate(encryptText(result));
  };

  const resetEvaluation = () => {
    setQuestions([]);
    setTime(0);
    setIsRunning(false);
    setButtonEvaluate(true);
    setButtonStartEvaluation(true);
    setScore(0);
    setCertificate("");
  };

  /* =======================
   EVALUATION SETUP
======================= */

  const fillQuestions = (data: IQuestionDto[]) => {
    const questionsTemp: IQuestionDto[] = [];

    for (let i = 0; i < data.length; i++) {
      questionsTemp.push({
        evaluationQuestionID: data[i].evaluationQuestionID,
        questionTypeID: data[i].questionTypeID,
        question: data[i].question,
        hint: data[i].hint,
        options: data[i].options,
        answerSelected: "",
      });
    }

    setQuestions(questionsTemp);
  };

  const validateEvaluation = async (courseId: number, isReinforcement: boolean) => {

    const timeRepeat = await courseService.getEvaluationTimeRepeat(courseId);

    if (timeRepeat !== 0) {
      return { blocked: true, timeRepeat };
    }

    const questions = await courseService.getEvaluationQuestion(
      courseId,
      isReinforcement
    );

    fillQuestions(questions);

    return { blocked: false };
  };

  /* =======================
   ANSWER HANDLERS
======================= */
  const handleChangeAnswers = (event: any, questionId: number) => {
    const questionsTemp = questions;
    const question = questionsTemp.find(
      (item) => item.evaluationQuestionID === questionId
    );
    if (question) {
      question.answerSelected = event.target.value;
      setQuestions([...questionsTemp]);
    }
  };

  const handleChangeSelectEvaluation = (
    event: any,
    optionID: number,
    questionId: number
  ) => {
    const questionsTemp = questions;
    const question = questionsTemp.find(
      (item) => item.evaluationQuestionID === questionId
    );
    if (question) {
      if (question.answerSelected) {
        let arrayTemp = question.answerSelected.split(",");
        if (
          arrayTemp.some((item) => item.substring(0, 3) === String(optionID))
        ) {
          let item = arrayTemp.find(
            (item) => item.substring(0, 3) === String(optionID)
          );
          let index = arrayTemp.indexOf(item!);
          arrayTemp.splice(index);
          arrayTemp.push(`${optionID}-${event.value}`);
          question.answerSelected = arrayTemp.toString();
        } else {
          arrayTemp.push(`${optionID}-${event.value}`);
          question.answerSelected = arrayTemp.toString();
        }
      } else {
        question.answerSelected = `${optionID}-${event.value}`;
      }
      setQuestions([...questionsTemp]);
    }
  };

  const handleChangeCheckbox = (event: any, questionId: number) => {
    const questionsTemp = questions;
    const question = questionsTemp.find(
      (item) => item.evaluationQuestionID === questionId
    );
    if (question) {
      if (question.answerSelected) {
        let arrayTemp = question.answerSelected.split(",");
        if (arrayTemp.includes(event.target.value)) {
          let index = arrayTemp.indexOf(event.target.value);
          arrayTemp.splice(index);
          question.answerSelected = arrayTemp.toString();
        } else {
          arrayTemp.push(event.target.value);
          question.answerSelected = arrayTemp.toString();
        }
      } else {
        question.answerSelected = event.target.value;
      }
      setQuestions([...questionsTemp]);
    }
  };

  /* =======================
   EVALUATION FLOW
======================= */
  const evaluateCourse = async (
    questions: IQuestionDto[],
    courseId: number,
    isReinforcement: boolean
  ) => {
    let answers: IAnswerCourseIn[] = [];

    for (let i = 0; i < questions.length; i++) {
      if (
        questions[i].questionTypeID === 2 ||
        questions[i].questionTypeID === 4
      ) {
        answers.push({
          QuestionID: questions[i].evaluationQuestionID,
          QuestionType: questions[i].questionTypeID,
          AnswerID: [questions[i].answerSelected?.slice(6)!],
        });
      } else if (questions[i].questionTypeID === 3) {
        answers.push({
          QuestionID: questions[i].evaluationQuestionID,
          QuestionType: questions[i].questionTypeID,
          AnswerID: questions[i].answerSelected!.split(","),
        });
      } else {
        answers.push({
          QuestionID: questions[i].evaluationQuestionID,
          QuestionType: questions[i].questionTypeID,
          AnswerID: [questions[i].answerSelected!],
        });
      }
    }

    const answersIn: IEvaluationCourseIn = {
      CourseID: courseId,
      Answer: answers,
    };

    let result;

    isReinforcement
      ? (result =
          await courseService.getCourseReinforcementEvaluationResult(answersIn))
      : (result = await courseService.getCourseEvaluationResult(answersIn));

    return {
      approved: isReinforcement ? result.isApproved : result.courseApproved,
      score: result.evaluationResult,
    };
  };

  const loseEvaluation = async (courseId: number, isReinforcement: boolean) => {
    const badAnswer: IAnswerCourseIn[] = [
      {
        QuestionID: 0,
        QuestionType: 0,
        AnswerID: ["0"],
      },
    ];

    const badAnswerIn: IEvaluationCourseIn = {
      CourseID: courseId,
      Answer: badAnswer,
    };

    isReinforcement
      ? await courseService.getCourseReinforcementEvaluationResult(badAnswerIn)
      : await courseService.getCourseEvaluationResult(badAnswerIn);
  };

  /* =======================
     RETURN
  ======================== */

  return {
    // questions
    questions,

    // buttons
    buttonStartEvaluation,
    setButtonStartEvaluation,
    buttonEvaluate,

    // result
    score,
    setScore,
    certificate,

    // timer
    setIsRunning,
    minutes,
    seconds,

    // actions
    resetEvaluation,
    loadCertificate,
    validateEvaluation,
    evaluateCourse,
    loseEvaluation,
    handleChangeCheckbox,
    handleChangeAnswers,
    handleChangeSelectEvaluation,
  };
};
